/**
 * Bulgarian. The primary interface language and the authoritative wording.
 *
 * Typed as `Record<MessageKey, string>`, so adding a key in
 * `src/shared/message-keys.ts` fails the typecheck until it is translated
 * here and in `en.ts`. That is the point: it is the only thing that stops a
 * half-translated page shipping.
 */

import type { MessageKey } from '../../shared/message-keys.ts'
import { SITE_NOTICE } from '../../shared/site-notice.ts'

export const bg: Record<MessageKey, string> = {
  'app.name': 'Чети по реда',
  'app.tagline': 'От коя книга да започна?',
  // The site-wide notice has one home, shared with the emails.
  'app.footer.disclaimer': SITE_NOTICE.bg,
  'app.footer.language': 'English',
  'app.loading': 'Зарежда се…',

  'register.title': 'Регистрация',
  'register.lead': 'Нужни са само имейл адрес и парола. Нищо друго не събираме.',
  'register.email.label': 'Имейл адрес',
  'register.email.hint': 'На този адрес ще изпратим писмо за потвърждение.',
  'register.password.label': 'Парола',
  'register.password.hint': 'Поне 12 знака.',
  'register.nickname.label': 'Псевдоним',
  'register.nickname.hint': 'По желание. Виждате го само вие, освен ако напишете отзив: тогава се показва до него.',
  'register.submit': 'Създайте профил',
  'register.submitting': 'Създава се…',
  'register.sent.title': 'Проверете пощата си',
  'register.sent.body':
    'Изпратихме писмо с връзка за потвърждение. Връзката работи еднократно и изтича след 24 часа. Ако писмото го няма, проверете и папката със спам.',
  'register.have_account': 'Вече имате профил? Влезте.',

  'verify.success.title': 'Адресът е потвърден',
  'verify.success.body': 'Вече можете да влезете в профила си.',
  'verify.failed.title': 'Връзката не работи',
  'verify.failed.body':
    'Връзката е използвана или е изтекла. Поискайте ново писмо и опитайте пак.',
  'verify.resend': 'Изпратете ново писмо',
  'verify.resent': 'Ако адресът съществува и чака потвърждение, изпратихме ново писмо.',

  'signin.title': 'Вход',
  'signin.email.label': 'Имейл адрес',
  'signin.password.label': 'Парола',
  'signin.submit': 'Влезте',
  'signin.submitting': 'Влизане…',
  'signin.forgot': 'Забравена парола',
  'signin.no_account': 'Нямате профил? Регистрирайте се.',

  'forgot.title': 'Забравена парола',
  'forgot.lead': 'Въведете адреса си и ще изпратим връзка за нова парола.',
  'forgot.submit': 'Изпратете връзка',
  'forgot.sent.title': 'Проверете пощата си',
  // The maintainer's wording (issue #40), then the link's lifetime, which the
  // screen already said. Identical whether or not the account exists.
  'forgot.sent.body':
    'Ако има регистрация с този адрес, изпратихме връзка за нова парола. Писмото пристига до няколко минути — проверете и папката със спам. Ако нищо не дойде, вероятно нямате регистрация с този адрес. Връзката работи еднократно и изтича след един час.',

  'reset.title': 'Нова парола',
  'reset.lead': 'Изберете нова парола. Това ще прекрати всички други сесии.',
  'reset.password.label': 'Нова парола',
  'reset.submit': 'Запазете паролата',
  'reset.success.title': 'Паролата е сменена',
  'reset.success.body': 'Влезте с новата си парола.',

  'account.title': 'Профил',
  'account.signed_in_as': 'Влезли сте като',
  'account.verified': 'Адресът е потвърден.',
  'account.signout': 'Изход',
  'account.nickname.save': 'Запазете псевдонима',
  'account.delete.title': 'Изтриване на профила',
  'account.delete.warning':
    'Изтриването е окончателно. Профилът, сесиите и всичко свързано с него изчезват и не могат да бъдат възстановени.',
  'account.delete.confirm_label': 'За потвърждение напишете имейл адреса си',
  'account.delete.submit': 'Изтрийте профила',
  'account.delete.done': 'Профилът е изтрит.',

  'nav.label': 'Основна навигация',
  'nav.world': 'Свят',
  'worlds.lead': 'Ред на четене за големите фентъзи светове: откъде да започнете, как да продължите и кои книги ги има на български.',
  'worlds.counts': 'Книги: {works} · Пътища за четене: {paths}',
  'worlds.next.title': 'Кой свят да е следващият?',
  'worlds.next.body': 'Първият свят вече е тук.',
  'page.not_found.title': 'Няма такава страница',
  'page.not_found.body': 'Адресът не води до страница от този сайт.',
  'world.not_found.title': 'Няма такъв свят',
  'world.not_found.body': 'Адресът не води до свят от този сайт.',
  'world.all': 'Всички светове',
  'nav.home': 'Начало',
  'nav.series': 'Поредици',
  'nav.books': 'Всички книги',

  'home.choose': 'Изберете път',
  'home.all_books': 'Всички {count} книги',
  'catalogue.not_loaded.title': 'Каталогът още не е зареден',
  'catalogue.not_loaded.body': 'Книгите още не са публикувани. Опитайте отново по-късно.',
  'catalogue.error': 'Каталогът не може да се зареди в момента. Презаредете страницата.',
  'series.index.title': 'Поредиците',
  'series.index.lead': 'Всяка поредица се чете в свой ред. Изберете една и започнете от първата ѝ книга.',
  'series.reading_order': 'Ред на четене',
  // Above a path's title: {stops} is „9 спирки“.
  'series.kicker': 'Път · {stops}',
  // A path's books are its stops: the card counts them as such.
  'series.stops.one': '{count} спирка',
  'series.stops.other': '{count} спирки',
  'books.count.one': '{count} книга',
  'books.count.other': '{count} книги',
  // Under „Извън поредиците“ on the world's home: {books} is „4 книги“.
  'outside.meta': '{books} · не са път',
  'series.all': 'Всички поредици',
  'series.not_found.title': 'Няма такава поредица',
  'series.not_found.body': 'Адресът не води до поредица. Ето всички:',
  'outside.lead': 'Тези книги не са част от нито един ред на четене. Всяка се чете самостоятелно, когато пожелаете.',
  'books.title': 'Всички книги по реда на издаване',
  'book.not_found.title': 'Няма такава книга',
  'book.not_found.body': 'Адресът не води до книга. Ето всички книги:',
  'book.year': 'Първо издание',
  'book.series': 'Поредица',
  'book.position': '{position} от {count}',
  'book.path.label': 'Мястото ѝ в „{series}“',
  'book.path.nearby': 'Пътят наблизо',
  // {stops} is „9 спирки“.
  'book.path.whole': 'Целият път — {stops}',
  'book.publication_order': 'По реда на издаване',
  'book.breadcrumb.stop': 'спирка {position} от {count}',
  // {ordinal} is feminine, agreeing with „спирка“: see i18n/ordinals.ts.
  'book.why': 'Защо е {ordinal} спирка',
  'book.why.numbered': 'Защо е спирка {position}',
  'book.outside': 'Тази книга не е част от нито един ред на четене — чете се самостоятелно.',
  'book.buy': 'Къде да я купите',
  'book.availability.none': 'Не се предлага в момента',
  'book.availability.unknown': 'Няма данни дали се предлага в момента.',
  'edition.paper.bg': 'Хартиено издание на български',
  'edition.ebook.bg': 'Е-книга на български',
  'edition.audio.bg': 'Аудиокнига на български',
  'edition.paper.en': 'Хартиено издание на английски',
  'edition.ebook.en': 'Е-книга на английски',
  'edition.audio.en': 'Аудиокнига на английски',
  'edition.other': 'Издание',

  'error.invalid_email': 'Това не изглежда като валиден имейл адрес.',
  'error.password_too_short': 'Паролата трябва да е поне 12 знака.',
  'error.password_too_long': 'Паролата е твърде дълга.',
  'error.password_required': 'Въведете парола.',
  'error.invalid_credentials': 'Грешен имейл адрес или парола.',
  'error.email_not_verified': 'Адресът още не е потвърден. Проверете пощата си.',
  'error.email_taken': 'Проверете пощата си.',
  'error.token_invalid': 'Връзката не е валидна.',
  'error.token_expired': 'Връзката е изтекла.',
  'error.rate_limited': 'Твърде много опити. Опитайте отново по-късно.',
  'error.turnstile_failed': 'Проверката не мина. Презаредете страницата и опитайте пак.',
  'error.turnstile_missing': 'Изчакайте проверката да завърши и опитайте пак.',
  'error.message_required': 'Напишете съобщение.',
  'error.message_too_long': 'Съобщението е твърде дълго.',
  'error.note_too_long': 'Бележката е твърде дълга: до 2000 знака.',
  'error.nickname_invalid': 'Псевдонимът може да е до 40 знака и не може да съдържа „@“.',
  'error.review_required': 'Напишете отзива си.',
  'error.review_too_long': 'Отзивът е твърде дълъг: до 6000 знака.',
  'error.reviews_suspended': 'Не можете да пишете и редактирате отзиви, докато трае спирането.',
  'error.reviews_banned': 'Вече не можете да пишете и редактирате отзиви.',
  'error.publish_no_note': 'Няма запазена бележка, която да публикувате.',
  'error.publish_too_long': 'Отзивът ще стане {total} знака, а ограничението е {limit}. Съкратете бележката или отзива и опитайте пак.',
  'error.publish_conflict': 'Отзивът или бележката се промениха междувременно. Презаредете страницата и опитайте пак.',
  'error.reason_required': 'Напишете причината.',
  'error.reason_too_long': 'Причината е твърде дълга: до 1000 знака.',
  'error.not_moderator': 'Само поддържащият сайта може да премахва отзиви.',
  'error.contact_failed': 'Съобщението не беше изпратено. Опитайте отново след малко.',
  'error.verification_mail_failed': 'Не успяхме да изпратим писмото за потвърждение. Опитайте отново.',

  'terms.title': 'Общи условия',
  'privacy.title': 'Поверителност',
  'about.title': 'За сайта',
  // Plural, as Bulgarian names this page. The URL stays /contact.
  'contact.title': 'Контакти',
  'content.updated': 'Последна промяна: {date}',
  'content.translation': 'Английският текст е превод за удобство. Валиден е българският.',
  'contact.lead': 'Грешка в реда, свят, който да добавим, или нещо друго — пишете ни.',
  'report.link': 'Съобщете за грешка на тази страница',
  'report.about': 'Относно: {page}',
  'contact.name.label': 'Име (по желание)',
  'contact.email.label': 'Имейл (по желание)',
  'contact.email.hint': 'Само за да можем да ви отговорим.',
  'contact.message.label': 'Съобщение',
  'contact.submit': 'Изпратете',
  'contact.sending': 'Изпраща се…',
  'contact.sent.title': 'Съобщението е изпратено',
  'contact.sent.body': 'Благодарим. Ще ви отговорим на адреса, който оставихте.',
  'contact.sent.body_anonymous': 'Благодарим. Не оставихте адрес, така че не можем да ви отговорим, но съобщението ще бъде прочетено.',
  'footer.nav': 'Страници за сайта',
  'meta.about.title': 'За сайта',
  'meta.about.description': 'Какво е „Чети по реда“, как е направен и кой го поддържа.',
  'error.not_signed_in': 'Трябва да влезете в профила си.',
  'error.confirmation_mismatch': 'Това не е имейл адресът на профила.',
  'error.bad_request': 'Нещо в заявката не е наред.',
  'error.unexpected': 'Нещо се обърка. Опитайте отново.',

  // Page metadata: the Worker writes these into each page's <head>, where a
  // crawler or a link preview reads them. A world's own text comes from the
  // catalogue; only site-level pages have strings of their own here.
  'meta.worlds.title': 'Чети по реда — редът за четене на фентъзи светове',
  'meta.worlds.description': 'Големите фентъзи светове рядко се четат по реда, по който са писани. Тук за всеки свят има пътища — коя книга е първа, коя след нея и защо.',
  'meta.world.title': '{name} — от коя книга да започнете',
  'meta.series_index.title': 'Поредиците — {name}',
  'meta.series.title': '{name} — ред за четене',
  'meta.books.title': 'Всички книги — {name}',
  'meta.book.title': '{title} — {series}, спирка {position} от {count}',
  'meta.quiz.title': 'Кой път е за вас — кратък тест',
  'meta.quiz.description': 'Десет въпроса. На края — откъде да започнете и защо точно оттам.',
  'reviews.title': 'Отзиви от читатели',
  'reviews.none': 'Още няма отзиви за тази книга.',
  'reviews.signin': 'Влезте, за да напишете отзив',
  'reviews.write': 'Напишете отзив',
  'reviews.edit': 'Редактирайте отзива си',
  'reviews.body.label': 'Вашият отзив',
  'reviews.body.hint': 'Публикува се веднага, с псевдонима ви до него, ако имате. Без разкриване на сюжета, освен ако отбележите полето отдолу.',
  'reviews.spoilers.label': 'Съдържа разкриване на сюжета',
  'reviews.publish': 'Публикувайте',
  'reviews.cancel': 'Откажете',
  'reviews.delete': 'Изтрийте отзива си',
  'reviews.suspended': 'Не можете да пишете и редактирате отзиви до {date} българско време, защото два ваши отзива бяха премахнати.',
  'reviews.banned': 'След третото премахване на ваш отзив вече не можете да пишете и редактирате отзиви.',
  'reviews.anonymous': 'Читател',
  'reviews.yours': 'вашият отзив',
  'reviews.spoilers.show': 'Разкрива сюжета — покажи отзива',
  'reviews.report': 'Докладвай',
  'reviews.report.reason': 'Какво не е наред (по желание)',
  'reviews.report.send': 'Изпратете доклада',
  'reviews.report.done': 'Благодарим. Докладът е изпратен.',
  'reviews.remove': 'Премахни',
  'reviews.remove.reason': 'Причина за премахването',
  'reviews.remove.hint': 'Задължителна. Авторът я получава по имейл.',
  'reviews.remove.confirm': 'Премахнете отзива',
  'reviews.remove.done': 'Отзивът е премахнат и авторът е уведомен.',
  'reviews.remove.done_unnotified': 'Отзивът е премахнат, но писмото до автора не тръгна.',
  'nav.quiz': 'Тест',
  'header.signed_in': 'Влезли сте:',
  'quiz.kicker': 'Тест · {count} въпроса, около две минути',
  'quiz.title': 'Коя поредица да е първата ви?',
  'quiz.count': 'Отговорихте на {answered} от {total}',
  'quiz.submit': 'Покажи резултата',
  'quiz.enough': 'Достатъчно — покажи резултат',
  'quiz.too_few': 'Отговорете поне на {min} въпроса, за да има резултат.',
  'quiz.result.heading': 'Вашият резултат',
  'quiz.result.clear': 'Най-добре ви пасва {first}. Ако не ви звучи, пробвайте {second}.',
  'quiz.result.close': 'Две поредици ви пасват почти еднакво: {first} и {second}.',
  'quiz.result.tie': 'Две поредици ви пасват еднакво: {first} и {second}.',
  'quiz.result.none': 'Отговорите ви не сочат към определена поредица. Разгледайте всички и изберете по описанието.',
  'quiz.result.based_on': 'На базата на {answered} от {total} отговора.',
  'quiz.result.why': 'Избрахме я, защото {reasons}.',
  'quiz.result.and': 'и',
  'quiz.result.start': 'Започнете с {book}.',
  'quiz.result.all': 'Покажи ми всички {count}',
  'quiz.share.title': 'Споделете резултата',
  'quiz.share.copy': 'Копирай връзката',
  'quiz.share.copied': 'Копирано',
  'quiz.share.failed': 'Не успях да копирам. Изберете връзката отдолу и я копирайте.',
  'quiz.share.label': 'Връзка към този резултат',
  'meta.quiz.result.title': 'Първата ми поредица: {first}',
  'meta.quiz.result.description': 'Десет въпроса за това от коя поредица на {world} да започнете. Моят резултат: {first}, после {second}.',
  'shelf.title': 'Моят рафт',
  'shelf.signed_out': 'С профил можете да отбелязвате кои книги искате да прочетете, кои четете, кои сте прочели и кои имате, и да следите докъде сте стигнали в дадена поредица.',
  'shelf.load_failed': 'Рафтът ви не се зареди. Опитайте отново след малко.',
  'shelf.status.legend': 'Четене',
  'shelf.status.want': 'Искам да я прочета',
  'shelf.status.reading': 'Чета я',
  'shelf.status.read': 'Прочетох я',
  'shelf.status.none': 'Нищо от тези',
  'shelf.owned': 'Имам я',
  'shelf.rating.legend': 'Моята оценка',
  'shelf.rating.none': 'Без оценка',
  'shelf.note.label': 'Бележка',
  'shelf.note.hint': 'Виждате я само вие.',
  'shelf.saving': 'Запазва се…',
  'shelf.saved': 'Запазено.',
  'shelf.undo': 'Отмени',
  'shelf.save_text': 'Запазете оценката и бележката',
  'shelf.saved_text': 'Оценката и бележката са запазени',
  'shelf.saved_rating': 'Оценката е запазена',
  'shelf.saved_note': 'Бележката е запазена',
  'shelf.cleared': 'Оценката и бележката са изчистени',
  // The button and its helper line say what happens before the click.
  'shelf.publish.create': 'Публикувайте бележката като отзив',
  'shelf.publish.create_hint': 'Текстът на бележката става ваш публичен отзив за книгата, а полето за бележка се изчиства за нови бележки.',
  'shelf.publish.append': 'Добавете бележката към отзива си',
  'shelf.publish.append_hint': 'Бележката се добавя в края на публикувания ви отзив, а полето за бележка се изчиства за нови бележки. Отзивът ще стане {total} от {limit} знака.',
  'shelf.publish.save_first': 'Запазете бележката, преди да я публикувате.',
  'shelf.publish.created': 'Бележката е публикувана като отзив. Ще намерите отзива по-долу на тази страница и в „Моите отзиви“ в профила си.',
  'shelf.publish.appended': 'Бележката е добавена към отзива ви. Ще намерите отзива по-долу на тази страница и в „Моите отзиви“ в профила си.',
  'shelf.publish.kept': 'Отзивът е публикуван, но бележката беше променена междувременно, затова е запазена.',
  'series.progress': 'Прочели сте {read} от {count}.',
  'account.shelf.title': 'Моите книги',
  'account.reviews.title': 'Моите отзиви',
  'account.reviews.empty': 'Още не сте написали отзив. Пишете го от страницата на книгата.',
  'account.shelf.empty': 'Още нямате отбелязани книги. Отбелязвате ги от страницата на всяка книга.',
  'account.shelf.want': 'Искам да прочета',
  'account.shelf.reading': 'Чета',
  'account.shelf.read': 'Прочетени',
  'account.shelf.owned': 'Имам',
  'account.shelf.progress': 'Докъде сте стигнали',
  'account.shelf.read_mark': 'прочетена:',
  'account.quiz.title': 'Тестът',
  'account.quiz.result': 'Първата ви поредица: {first}. След нея: {second}.',
  'account.quiz.taken': 'Направен на {date}',
  'account.quiz.again': 'Направете го отново',
  'quiz.result.saved': 'Запазен в профила ви.',
  'meta.terms.title': 'Общи условия',
  'meta.terms.description': 'Условията за използване на сайта.',
  'meta.privacy.title': 'Поверителност',
  'meta.privacy.description': 'Какви данни събира сайтът и какви не.',
  'meta.contact.title': 'Контакти',
  'meta.contact.description': 'Пишете ни — грешка в реда, свят, който да добавим, или нещо друго.',
  'meta.verify.title': 'Потвърждение на адреса',
  'meta.not_found.title': 'Страницата не е намерена',
  'meta.image.alt': 'Чети по реда — редът за четене на големите фентъзи светове',
}
